import ResizeObserver from 'resize-observer-polyfill';

export enum DomOrigin {
  REQUEST_SERVER = 'request-server',
  CONTENT_SERVER = 'content-server',
}

type HTMLElementContent = string | { toString: () => string };

interface GlobalConfig {
  alternates: string;
  baseDomain: string;
  brightcoveAccountId: string;
  brightcovePlayerId: string;
  cludoCustomerId: string;
  cludoEngineId: string;
  cognitoUserpoolClientId: string;
  cognitoUserpoolId: string;
  componentLibraryUrl: string;
  domOrigin: DomOrigin;
  formsApiUrl: string;
  gapiClientId: string;
  gapiKey: string;
  hubPages: string;
  headerIconUrl: string;
  isShareHidden: string;
  locale: string;
  nonDefaultThemeSubcategories: string,
  pageId: string;
  recaptchaKey: string;
  tagPageUrl: string;
  translationUrl: string;
  tagsTranslationUrl: string;
  popularSearchesTranslationUrl: string;
  whiteListedDomains: string;
  websiteName: string;
  cludoFuzziness: string;
  componentLibrary: string;
}

export enum Key {
  ENTER = 13,
  SPACE = 32,
  ESCAPE = 27,
  UP = 38,
  DOWN = 40,
  A = 65,
  Z = 90,
}

export const DomService = {
  createElement(tagName: string, content: HTMLElementContent = '', props: Record<string, string> = {}): HTMLElement {
    const element = document.createElement(tagName);
    element.innerHTML = typeof content === 'string' ? content : content.toString();
    Object.keys(props).forEach((prop) => {
      element.setAttribute(prop, props[prop]);
    });
    return element;
  },

  createObserver(element: HTMLElement, callback: ((_: ResizeObserverEntry[]) => void)) {
    if (!(element instanceof Element)) {
      return;
    }
    const observer = new ResizeObserver((entries) => callback(entries));
    observer.observe(element);
  },

  getGlobalConfig(): GlobalConfig {
    const element = document.body.querySelector('roche-global-config') as HTMLElement;
    return { ...(element ? element.dataset : {}) } as unknown as GlobalConfig;
  },

  getGlobalHeader(): HTMLRocheHeaderElement | null {
    return document.querySelector('roche-header');
  },

  getGlobalOnClickNotice(): HTMLRocheOnClickNoticeElement | null {
    return document.querySelector('roche-on-click-notice');
  },

  /**
   * Mimics Element.closest() but transverses Shadow Dom
   * https://stackoverflow.com/questions/54520554/custom-element-getrootnode-closest-function-crossing-multiple-parent-shadowd
   */
  closestElement(selector: string, base: Element = this): Element | null {
    // eslint-disable-next-line no-underscore-dangle
    function __closestFrom(el: Element | Window | Document): Element | null {
      if (!el || el === document || el === window) return null;
      const found = (el as Element).closest(selector);
      return found || __closestFrom(((el as Element).getRootNode() as ShadowRoot).host);
    }
    return __closestFrom(base);
  },

  lockBody() {
    document.body.style.height = '100%';
    document.body.style.overflow = 'hidden';
  },

  unlockBody() {
    document.body.style.height = '';
    document.body.style.overflow = '';
  },

  getLocationHostname(): string {
    return window.location.hostname;
  },

  /**
   * True if currently running on a mocked environment, like hydrate or tests
   * window.isPrerender is set on app.ts only on hydrate's runtime context.
   * It is specific to our app and not a stencil default.
   * More info https://stenciljs.com/docs/hydrate-app
   */
  isMockedContext(): boolean {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (window as any).isPrerender;
  },

  isRtl(element: HTMLElement) : boolean {
    const parentDirectionality = (DomService.closestElement('*[dir]', element) as HTMLElement)?.dir;

    return parentDirectionality === 'rtl';
  },

  handleTextDirection(element: HTMLElement): void {
    const parentDirectionality = (DomService.closestElement('*[dir]', element) as HTMLElement)?.dir;
    if (!parentDirectionality) {
      return;
    }

    element?.setAttribute('dir', parentDirectionality);
  },

};
